/**
 * The map, and the placement interaction on top of it.
 *
 * This is the plugin's `PlacePobTool` in a browser: a ghost polygon follows
 * the cursor, a click fixes the point of beginning, a slider turns the shape,
 * and Confirm commits it as a layer. Group mode places every tract sharing a
 * commencement monument off one click and rotates the assembly as a unit.
 *
 * The transform lives in `place.ts` and is tested against the plugin's own
 * numbers. This component does the DOM and the MapLibre sources; it computes
 * no geometry of its own, which is the same split that lets the plugin ship
 * without traverse math.
 */

import { useEffect, useRef, useState } from "preact/hooks";
import maplibregl, { type Map as MapLibreMap } from "maplibre-gl";
import { BASEMAPS, DEFAULT_BASEMAP, type BasemapId } from "./basemaps";
import { OVERLAYS } from "./overlays";
import { describeFeature, identifyPlss, type IdentifyResult } from "./identify";
import {
  boundsOf,
  placeRing,
  placeSegments,
  type LngLat,
  type Ring,
} from "../place";
import { VERDICT_COLOR } from "../types";
import type { PlacedTract } from "../exports";

type PolygonFeature = {
  type: "Feature";
  geometry: { type: "Polygon"; coordinates: [number, number][][] };
  properties: { verdict: string; tract: string };
};
type CourseFeature = {
  type: "Feature";
  geometry: { type: "LineString"; coordinates: [number, number][] };
  properties: { course: number };
};
type Drawn = { polygons: PolygonFeature[]; lines: CourseFeature[] };

const GHOST = "easting-ghost";
const PLACED = "easting-placed";
const COURSES = "easting-courses";

export interface MapViewProps {
  /** Non-null while the user is placing: the rings that follow the cursor. */
  pending: Ring[] | null;
  placed: PlacedTract[];
  rotationDeg: number;
  onPobPicked: (point: LngLat) => void;
  pob: LngLat | null;
  /** Points to bring into view: the positions a locate call just suggested.
   *  Null leaves the camera alone, which is what an unsuccessful locate and
   *  every other interaction want. */
  focus: LngLat[] | null;
}

function emptyCollection() {
  return { type: "FeatureCollection" as const, features: [] };
}

export function MapView({
  pending,
  placed,
  rotationDeg,
  onPobPicked,
  pob,
  focus,
}: MapViewProps) {
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<MapLibreMap | null>(null);
  const [basemap, setBasemap] = useState<BasemapId>(DEFAULT_BASEMAP);
  const [ready, setReady] = useState(false);
  // The map's own bearing and pitch, which are the viewer's and have nothing
  // to do with the parcel's rotation. Keeping them on screen is what stops
  // the two being confused.
  const [view, setView] = useState({ bearing: 0, pitch: 0 });
  // Reference layers, off by default: the parcel is the subject and the grid
  // is context somebody asks for.
  const [overlays, setOverlays] = useState<Set<string>>(new Set());
  // Identify is a mode rather than a permanent behaviour, because a click on
  // the map already means "put the point of beginning here" while placing.
  const [identifying, setIdentifying] = useState(false);
  const [found, setFound] = useState<IdentifyResult[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  // Tiles and the identify round trip both come from other people's servers,
  // over networks we do not control. Saying so beats a map that looks broken
  // while it is only slow.
  const [loadingTiles, setLoadingTiles] = useState(false);
  const [asking, setAsking] = useState(false);
  const [zoom, setZoom] = useState(3.6);
  const cursor = useRef<LngLat | null>(null);
  // What is currently drawn, plus the two bits of view state a style reload
  // has to restore. A style swap drops every source MapLibre holds, and the
  // effect that computes placed features only runs when `placed` changes, so
  // without these the map comes back empty while the app still believes the
  // tracts are on it. Refs rather than deps: reading them in the reload
  // handler must not make a style swap re-run when they change.
  const drawn = useRef<Drawn>({ polygons: [], lines: [] });
  const overlaysRef = useRef(overlays);
  overlaysRef.current = overlays;
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  useEffect(() => {
    if (!container.current || map.current) return;
    const instance = new maplibregl.Map({
      container: container.current,
      style: BASEMAPS[basemap].style,
      // Continental US. A deed arrives with no location by definition, so
      // there is nothing better to open on than the country most of them
      // come from.
      center: [-98.5, 39.8],
      zoom: 3.6,
      attributionControl: { compact: false },
    });
    // The compass shows the bearing and resets it on click; drag-rotate and
    // pitch stay on, because a hillside parcel reads better tilted.
    instance.addControl(
      new maplibregl.NavigationControl({
        showCompass: true,
        visualizePitch: true,
      }),
      "top-right",
    );
    instance.addControl(
      new maplibregl.ScaleControl({ unit: "imperial" }),
      "bottom-left",
    );
    instance.on("load", () => {
      addLayers(instance);
      setReady(true);
    });
    // `dataloading` fires per source request and `idle` once everything has
    // settled, which together are exactly "the map is still fetching".
    instance.on("dataloading", () => setLoadingTiles(true));
    instance.on("idle", () => setLoadingTiles(false));
    instance.on("error", () => setLoadingTiles(false));
    const track = () =>
      setView({
        bearing: Math.round(instance.getBearing()),
        pitch: Math.round(instance.getPitch()),
      });
    instance.on("rotate", track);
    instance.on("pitch", track);
    instance.on("zoom", () => setZoom(instance.getZoom()));
    map.current = instance;
    // A dev-only handle for the end-to-end suite. The placed tracts live in
    // a WebGL canvas with no DOM to assert against, and the failure worth
    // catching is the map losing features the app still believes are drawn,
    // so a test has to ask MapLibre itself rather than ask the app.
    if (import.meta.env.DEV) {
      (window as unknown as { __eastingMap?: MapLibreMap }).__eastingMap =
        instance;
    }
    return () => {
      instance.remove();
      map.current = null;
    };
    // The basemap is swapped through setStyle below rather than by rebuilding
    // the map, so this runs once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Swapping the basemap drops every source with it, so everything drawn goes
  // back on once the new style has loaded.
  //
  // `overlays` is deliberately NOT a dependency. It was one until 2026-08-13,
  // which meant toggling the PLSS grid ran setStyle and wiped every placed
  // tract off the map: the sources came back empty and nothing repainted them,
  // because the effect that computes them watches `placed`, which had not
  // changed. Overlays have their own effect below, and adding one has never
  // needed a style swap.
  useEffect(() => {
    const instance = map.current;
    if (!instance || !ready) return;
    instance.setStyle(BASEMAPS[basemap].style);
    instance.once("styledata", () => {
      repaint(
        instance,
        overlaysRef.current,
        drawn.current,
        selectedRef.current,
      );
    });
  }, [basemap, ready]);

  useEffect(() => {
    const instance = map.current;
    if (!instance || !ready) return;
    addOverlays(instance, overlays);
  }, [overlays, ready]);

  useEffect(() => {
    const instance = map.current;
    if (!instance || !ready || !instance.getLayer(`${PLACED}-selected`)) return;
    // A filter rather than a second source: the selection is a view of what
    // is already drawn, so it cannot drift from it.
    instance.setFilter(`${PLACED}-selected`, [
      "==",
      ["get", "tract"],
      selected ?? "\u0000",
    ]);
  }, [selected, ready]);

  useEffect(() => {
    const instance = map.current;
    if (!instance || !ready) return;
    const onMove = (event: maplibregl.MapMouseEvent) => {
      cursor.current = { lng: event.lngLat.lng, lat: event.lngLat.lat };
      if (pending && !pob)
        drawGhost(instance, pending, cursor.current, rotationDeg);
    };
    const onClick = (event: maplibregl.MapMouseEvent) => {
      if (pending) {
        onPobPicked({ lng: event.lngLat.lng, lat: event.lngLat.lat });
        return;
      }
      if (!identifying) return;
      void runIdentify(
        instance,
        event,
        overlays,
        setFound,
        setSelected,
        setAsking,
      );
    };
    instance.on("mousemove", onMove);
    instance.on("click", onClick);
    instance.getCanvas().style.cursor =
      pending || identifying ? "crosshair" : "";
    return () => {
      instance.off("mousemove", onMove);
      instance.off("click", onClick);
    };
  }, [pending, pob, rotationDeg, ready, onPobPicked, identifying, overlays]);

  // The rotation slider moves the ghost without another click.
  useEffect(() => {
    const instance = map.current;
    if (!instance || !ready) return;
    const origin = pob ?? cursor.current;
    if (pending && origin) drawGhost(instance, pending, origin, rotationDeg);
    if (!pending) setData(instance, GHOST, emptyCollection());
  }, [pending, pob, rotationDeg, ready]);

  // A suggestion nobody can see is not much of a suggestion: the map opens on
  // the continental US, and a deed arrives with no location, so the operator
  // would otherwise have to find Citrus County by hand to look at what the
  // service just proposed.
  useEffect(() => {
    const instance = map.current;
    if (!instance || !ready || !focus?.length) return;
    if (focus.length === 1) {
      // Parcel scale. Close enough to read the boundary against imagery,
      // wide enough that a suggestion off by a few hundred feet is still on
      // screen rather than mysteriously absent.
      instance.flyTo({
        center: [focus[0].lng, focus[0].lat],
        zoom: 16,
        duration: 900,
      });
      return;
    }
    const bounds = boundsOf([focus.map((p) => [p.lng, p.lat])]);
    if (bounds)
      instance.fitBounds(bounds, { padding: 120, maxZoom: 16, duration: 900 });
  }, [focus, ready]);

  useEffect(() => {
    const instance = map.current;
    if (!instance || !ready) return;
    const polygons: PolygonFeature[] = [];
    const lines: CourseFeature[] = [];
    for (const item of placed) {
      const rings = item.georefRings ?? [
        placeRing(item.ring, item.origin!, item.rotationDeg),
      ];
      polygons.push({
        type: "Feature" as const,
        geometry: { type: "Polygon" as const, coordinates: rings },
        properties: {
          verdict: item.ring.verdict.status,
          tract: item.ring.tract.name,
        },
      });
      if (item.origin) {
        // Numbered like the certificate figure: course N keys into the same
        // call table row there, so the two artifacts read as one.
        placeSegments(item.ring, item.origin, item.rotationDeg).forEach(
          (segment, index) => {
            lines.push({
              type: "Feature" as const,
              geometry: { type: "LineString" as const, coordinates: segment },
              properties: { course: index + 1 },
            });
          },
        );
      }
    }
    // Remembered before it is drawn, so a later style reload can put the same
    // features back without recomputing them.
    drawn.current = { polygons, lines };
    setData(instance, PLACED, {
      type: "FeatureCollection",
      features: polygons,
    });
    setData(instance, COURSES, { type: "FeatureCollection", features: lines });
    const bounds = boundsOf(
      polygons.flatMap((f) => f.geometry.coordinates as [number, number][][]),
    );
    if (bounds)
      instance.fitBounds(bounds, { padding: 80, maxZoom: 18, duration: 600 });
  }, [placed, ready]);

  return (
    <div class="map-wrap">
      <div ref={container} class="map" />
      <div class="basemap-toggle">
        {(Object.keys(BASEMAPS) as BasemapId[]).map((id) => (
          <button
            key={id}
            type="button"
            class={id === basemap ? "chip chip-on" : "chip"}
            onClick={() => setBasemap(id)}
          >
            {BASEMAPS[id].label}
          </button>
        ))}
        <span class="toggle-sep" />
        <button
          type="button"
          class={identifying ? "chip chip-on" : "chip"}
          title="Click the map to see what is there: a placed tract's attributes, and the PLSS section under it when that layer is on."
          onClick={() => {
            setIdentifying((on) => !on);
            setFound(null);
            setSelected(null);
          }}
        >
          Identify
        </button>
        <span class="toggle-sep" />
        {OVERLAYS.map((overlay) => (
          <button
            key={overlay.id}
            type="button"
            title={overlay.title}
            class={overlays.has(overlay.id) ? "chip chip-on" : "chip"}
            onClick={() =>
              setOverlays((current) => {
                const next = new Set(current);
                if (next.has(overlay.id)) next.delete(overlay.id);
                else next.add(overlay.id);
                return next;
              })
            }
          >
            {overlay.label}
          </button>
        ))}
      </div>
      {(view.bearing !== 0 || view.pitch !== 0) && (
        <button
          type="button"
          class="view-reset"
          title="The map view, not the parcel. Reset to north-up and flat."
          onClick={() => {
            map.current?.easeTo({ bearing: 0, pitch: 0, duration: 400 });
            setView({ bearing: 0, pitch: 0 });
          }}
        >
          <span class="mono">{formatBearing(view.bearing)}</span>
          {view.pitch !== 0 && <span class="mono"> · {view.pitch}° tilt</span>}
          <span class="reset-word">Reset view</span>
        </button>
      )}
      {pending && !pob && (
        <div class="map-hint">Click the point of beginning</div>
      )}
      {identifying && !pending && !found && !asking && (
        <div class="map-hint">Click anything on the map</div>
      )}
      {[...overlays]
        .map((id) => OVERLAYS.find((overlay) => overlay.id === id))
        .filter((overlay): overlay is (typeof OVERLAYS)[number] => !!overlay)
        .map((overlay) => (
          <div key={overlay.id} class="map-note">
            {zoom < overlay.minzoom ? overlay.hint : overlay.coverage}
          </div>
        ))}
      {loadingTiles && (
        <div class="map-loading" role="status">
          <span class="spinner" aria-hidden="true" />
          Loading map…
        </div>
      )}
      {found && (
        <div class="identify">
          <header>
            <strong>What is here</strong>
            <button
              type="button"
              class="ghost"
              onClick={() => {
                setFound(null);
                setSelected(null);
              }}
            >
              Close
            </button>
          </header>
          {asking && (
            <p class="muted small">
              <span class="spinner" aria-hidden="true" /> Asking the PLSS
              fabric…
            </p>
          )}
          {!asking && found.length === 0 && (
            <p class="muted small">Nothing on this spot.</p>
          )}
          {found.map((result, index) => (
            <section key={index}>
              <h4>{result.title}</h4>
              <dl>
                {result.rows.map((row) => (
                  <div key={row.label}>
                    <dt>{row.label}</dt>
                    <dd>{row.value}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

/** Answer a click: our own features first, then the fabric.
 *
 *  Ours first because a click that lands on a placed tract almost certainly
 *  means that tract, and because the local query is instant while the service
 *  round trip is not. */
async function runIdentify(
  instance: MapLibreMap,
  event: maplibregl.MapMouseEvent,
  overlays: Set<string>,
  setFound: (results: IdentifyResult[]) => void,
  setSelected: (tract: string | null) => void,
  setAsking: (busy: boolean) => void,
): Promise<void> {
  const layers = [`${PLACED}-fill`, `${COURSES}-line`].filter((id) =>
    instance.getLayer(id),
  );
  const hits = layers.length
    ? instance.queryRenderedFeatures(event.point, { layers })
    : [];
  const results = hits.map((hit) => describeFeature(hit.properties ?? {}));
  setSelected(hits.length ? String(hits[0].properties?.tract ?? "") : null);
  setFound(results);

  if (!overlays.has("plss-sections")) return;
  setAsking(true);
  try {
    const bounds = instance.getBounds();
    const canvas = instance.getCanvas();
    const plss = await identifyPlss(
      { lng: event.lngLat.lng, lat: event.lngLat.lat },
      [
        bounds.getWest(),
        bounds.getSouth(),
        bounds.getEast(),
        bounds.getNorth(),
      ],
      [canvas.clientWidth, canvas.clientHeight],
    );
    setFound([...results, ...plss]);
  } catch {
    // The fabric being unreachable must not blank out what we did find.
    setFound([
      ...results,
      {
        title: "PLSS",
        rows: [{ label: "note", value: "the fabric did not answer" }],
      },
    ]);
  } finally {
    setAsking(false);
  }
}

/** The map's bearing as a compass reading. MapLibre counts degrees
 *  counter-clockwise from north, so a bearing of 30 means the map has been
 *  turned so that north points 30° to the right of up. */
function formatBearing(bearing: number): string {
  const normalized = ((bearing % 360) + 360) % 360;
  return normalized === 0 ? "north up" : `${normalized}° from north`;
}

/** Reference layers under the parcel and over the basemap.
 *
 *  Inserted beneath the placed-tract layers by id, so turning the grid on can
 *  never hide the thing the operator is looking at. */
function addOverlays(instance: MapLibreMap, enabled: Set<string>): void {
  for (const overlay of OVERLAYS) {
    const layerId = `${overlay.id}-raster`;
    const wanted = enabled.has(overlay.id);
    const present = !!instance.getLayer(layerId);
    if (wanted && !present) {
      if (!instance.getSource(overlay.id)) {
        instance.addSource(overlay.id, {
          type: "raster",
          tiles: overlay.tiles,
          tileSize: overlay.tileSize,
          minzoom: overlay.minzoom,
          attribution: overlay.attribution,
        });
      }
      const before = instance.getLayer(`${PLACED}-fill`)
        ? `${PLACED}-fill`
        : undefined;
      instance.addLayer(
        {
          id: layerId,
          type: "raster",
          source: overlay.id,
          paint: { "raster-opacity": 0.85 },
        },
        before,
      );
    } else if (!wanted && present) {
      instance.removeLayer(layerId);
    }
  }
}

/** Put everything back after a style reload, in the order the map needs it.
 *
 *  Sources and layers first, then the data, then the selection filter, which
 *  is a filter on a layer that has just been recreated and would otherwise
 *  come back showing nothing highlighted. */
export function repaint(
  instance: MapLibreMap,
  enabled: Set<string>,
  features: Drawn,
  selected: string | null,
): void {
  addOverlays(instance, enabled);
  addLayers(instance);
  setData(instance, PLACED, {
    type: "FeatureCollection",
    features: features.polygons,
  });
  setData(instance, COURSES, {
    type: "FeatureCollection",
    features: features.lines,
  });
  if (instance.getLayer(`${PLACED}-selected`)) {
    instance.setFilter(`${PLACED}-selected`, [
      "==",
      ["get", "tract"],
      selected ?? "\u0000",
    ]);
  }
}

function addLayers(instance: MapLibreMap): void {
  for (const id of [GHOST, PLACED, COURSES]) {
    if (!instance.getSource(id)) {
      instance.addSource(id, { type: "geojson", data: emptyCollection() });
    }
  }
  if (!instance.getLayer(`${PLACED}-fill`)) {
    instance.addLayer({
      id: `${PLACED}-fill`,
      type: "fill",
      source: PLACED,
      paint: {
        // The verdict colours are the plugin's, so a REVIEW tract looks like
        // a REVIEW tract wherever someone meets it.
        "fill-color": [
          "match",
          ["get", "verdict"],
          "PASS",
          VERDICT_COLOR.PASS,
          "REVIEW",
          VERDICT_COLOR.REVIEW,
          VERDICT_COLOR.FAIL,
        ],
        "fill-opacity": 0.25,
      },
    });
    instance.addLayer({
      id: `${PLACED}-selected`,
      type: "line",
      source: PLACED,
      filter: ["==", ["get", "tract"], "\u0000"],
      paint: { "line-color": "#1d5fbf", "line-width": 5, "line-opacity": 0.9 },
    });
    instance.addLayer({
      id: `${PLACED}-line`,
      type: "line",
      source: PLACED,
      paint: { "line-color": "#14161a", "line-width": 2 },
    });
    instance.addLayer({
      id: `${COURSES}-line`,
      type: "line",
      source: COURSES,
      paint: {
        "line-color": "#1d5fbf",
        "line-width": 1,
        "line-dasharray": [3, 2],
      },
    });
    instance.addLayer({
      id: `${COURSES}-label`,
      type: "symbol",
      source: COURSES,
      layout: {
        "text-field": ["to-string", ["get", "course"]],
        "text-size": 11,
        "symbol-placement": "line-center",
        // The certificate offsets its numbers off the line for the same
        // reason: a digit sitting on the stroke reads as part of it.
        "text-offset": [0, -0.7],
      },
      paint: {
        "text-color": "#1d5fbf",
        "text-halo-color": "#ffffff",
        "text-halo-width": 1.2,
      },
    });
    instance.addLayer({
      id: `${GHOST}-fill`,
      type: "fill",
      source: GHOST,
      paint: { "fill-color": "#1d5fbf", "fill-opacity": 0.25 },
    });
    instance.addLayer({
      id: `${GHOST}-line`,
      type: "line",
      source: GHOST,
      paint: { "line-color": "#1d5fbf", "line-width": 2 },
    });
  }
}

function drawGhost(
  instance: MapLibreMap,
  rings: Ring[],
  origin: LngLat,
  rotationDeg: number,
): void {
  setData(instance, GHOST, {
    type: "FeatureCollection",
    features: rings.map((ring) => ({
      type: "Feature" as const,
      geometry: {
        type: "Polygon" as const,
        coordinates: [placeRing(ring, origin, rotationDeg)],
      },
      properties: {},
    })),
  });
}

function setData(instance: MapLibreMap, id: string, data: unknown): void {
  const source = instance.getSource(id) as maplibregl.GeoJSONSource | undefined;
  source?.setData(data as never);
}
